import round from 'lodash/round';
import type { Moment } from 'moment/moment';

import { CLEANING_PRODUCTS, CLEANING_RECURRING, CLEANING_SPOT } from '../constants/prestations';
import { HOURLY_SURCHARGE } from '../constants/prices';
import type {
  BasketContent,
  CleaningBasketPrices,
  CleaningPrestation,
  CleaningPrestationOption,
  GiftCard,
  PrestationReference,
  Promotion,
  Universe,
  ZoneId,
} from '../types/commons';
import { getAllBasketPrestations } from '../utils/basket';
import { isCountryGB } from '../utils/country';
import { findPrestation } from '../utils/prestations';
import {
  calculatePromoAndGiftValues,
  getAppointmentSurchargeReason,
  getPriceByZoneOrPriceItself,
} from './common-prices';

export const DEFAULT_CLEANING_BASKET_PRICES: CleaningBasketPrices = {
  kind: 'cleaning',
  totalPriceAfterTax: 0,
  totalPriceToShow: 0,
  hourlyPrice: 0,
  totalHourlyPrice: 0,
  totalHourlyPriceAfterTax: 0,
  totalPrice: 0,
  minPriceSurcharge: 0,
  tipAmount: 0,
  membership: {
    reduction: 0,
    hourlyPriceReduction: 0,
    totalPriceAfterTax: 0,
    totalPriceToShow: 0,
    hourlyPrice: 0,
    totalHourlyPrice: 0,
    totalHourlyPriceAfterTax: 0,
    totalPrice: 0,
    minPriceSurcharge: 0,
    tipAmount: 0,
  },
};

export const getPriceAfterTax = (price: number) => price / 2;

const getCleaningPrestation = (prestations: PrestationReference[], universe: Universe) => {
  const prestationReference = prestations.find(
    (reference: PrestationReference) => reference === CLEANING_RECURRING || reference === CLEANING_SPOT,
  );
  const prestation = prestationReference ? findPrestation(prestationReference, universe) : undefined;
  // convert null to undefined
  return prestation || undefined;
};

const getProductsPrestation = (prestations: PrestationReference[], universe: Universe) => {
  const prestationReference = prestations.find((reference: PrestationReference) => reference === CLEANING_PRODUCTS);
  const prestation = prestationReference ? findPrestation(prestationReference, universe) : undefined;
  // convert null to undefined
  return prestation || undefined;
};

const getTaxAmount = (isDurationDefined: boolean, totalHourlyPriceAfterTax: number, totalPrice: number) => {
  if (isCountryGB()) return 0;
  return isDurationDefined ? totalPrice / 2 : totalHourlyPriceAfterTax;
};

type ComputeCleaningPricesForPrestationPriceParams = {
  cleaningPrestationPrice: number;
  productPrestationPrice: number;
  durationInHours: number;
  primaryAppointment: Moment | null;
  universe: Universe;
  promotion?: Promotion | null;
  giftCard?: GiftCard;
  tipAmount: number;
  isDurationDefined: boolean;
};

type ComputeCleaningPricesForPrestationPriceResponse = Omit<CleaningBasketPrices, 'kind' | 'membership'>;

const computeCleaningPricesForPrestationPrice = ({
  cleaningPrestationPrice,
  productPrestationPrice,
  durationInHours,
  isDurationDefined,
  primaryAppointment,
  universe,
  promotion,
  giftCard,
  tipAmount,
}: ComputeCleaningPricesForPrestationPriceParams): ComputeCleaningPricesForPrestationPriceResponse => {
  const { holidays } = universe;

  // If we don't have the product prestation, the productsPrice is equal to 0, this is why we always add it
  const prestationsHourlyRawPrice = cleaningPrestationPrice + productPrestationPrice;

  // Total price whithout adjustments
  const prestationsRawPrice = prestationsHourlyRawPrice * durationInHours;

  // Appointment surcharge
  const appointmentSurchargeReason = getAppointmentSurchargeReason(primaryAppointment, holidays);
  const hasPriceWithSurcharge = prestationsRawPrice > 0 && appointmentSurchargeReason;
  const hourlyPriceSurcharge = hasPriceWithSurcharge ? HOURLY_SURCHARGE : undefined;
  const appointmentSurcharge = hourlyPriceSurcharge ? hourlyPriceSurcharge * durationInHours : 0;

  const totalPriceBeforeReductions = prestationsRawPrice + appointmentSurcharge;

  // Promo and gift card values
  const { promoValue, giftValue } = calculatePromoAndGiftValues({
    priceBeforeReduction: totalPriceBeforeReductions,
    promotion,
    giftCard,
    durationInHours,
  });

  const promotionAmountPerHour = promotion?.calculation_type === 'per_hour' ? promotion.amount : 0;

  const prestationsHourlyPriceWithSurcharge = Math.max(
    hasPriceWithSurcharge
      ? prestationsHourlyRawPrice + HOURLY_SURCHARGE - promotionAmountPerHour
      : prestationsHourlyRawPrice - promotionAmountPerHour,
    0,
  );

  const hasNoTax = isCountryGB() || giftValue > 0;
  const totalHourlyPriceAfterTax = hasNoTax
    ? prestationsHourlyPriceWithSurcharge
    : getPriceAfterTax(prestationsHourlyPriceWithSurcharge);

  // Total
  const totalPriceBeforeTax = Math.max(totalPriceBeforeReductions - (promoValue + giftValue), 0);

  const taxAmount = getTaxAmount(isDurationDefined, totalHourlyPriceAfterTax, totalPriceBeforeTax);

  const totalPriceToShow = isDurationDefined ? totalPriceBeforeTax : prestationsHourlyPriceWithSurcharge;
  const totalPriceAfterTax = totalPriceToShow - taxAmount;

  return {
    hourlyPrice: round(prestationsHourlyRawPrice),
    totalHourlyPrice: round(prestationsHourlyPriceWithSurcharge),
    totalHourlyPriceAfterTax: round(totalHourlyPriceAfterTax),
    totalPrice: round(totalPriceBeforeTax),
    minPriceSurcharge: 0,
    hourlyPriceSurcharge: hourlyPriceSurcharge ? round(hourlyPriceSurcharge) : undefined,
    appointmentSurcharge: round(appointmentSurcharge),
    appointmentSurchargeReason,
    promoValue: round(promoValue),
    giftValue: round(giftValue),
    tipAmount: round(tipAmount),
    prestationHourlyPrice: round(cleaningPrestationPrice),
    totalPriceAfterTax: round(totalPriceAfterTax),
    totalPriceToShow: round(totalPriceToShow),
    productPrestationHourlyPrice: round(productPrestationPrice),
    taxAmount: round(taxAmount),
  };
};

type ComputeCleaningPricesParams = {
  universe: Universe;
  primaryAppointment: Moment | null;
  basketContent: BasketContent;
  tipAmount?: number;
  zone: ZoneId;
  promotion?: Promotion | null;
  giftCard?: GiftCard;
  duration?: number | null;
};

// TODO: CLEANING_V2 remove this when done in mobile-pro
export const computeCleaningPrices = ({
  universe,
  primaryAppointment,
  basketContent,
  tipAmount = 0,
  zone,
  promotion,
  giftCard,
  duration,
}: ComputeCleaningPricesParams): CleaningBasketPrices => {
  // As duration can be undefined/null at beginning of funnel
  const isDurationDefined = !!duration;
  // We calculate with at least 2 hours for the duration
  // TODO: This is legacy and might need to get refactored when the price calculation is moved to the back
  // (e.g. applying fixed promo before duration is set)
  const durationInHours = (duration || 0) / 60 || 2;

  const basketPrestations = getAllBasketPrestations(basketContent);

  const cleaningPrestation = getCleaningPrestation(basketPrestations, universe);
  const productsPrestation = getProductsPrestation(basketPrestations, universe);

  const standardPrestationPrice = getPriceByZoneOrPriceItself(cleaningPrestation?.prices ?? 0, zone);
  const memberPrestationPrice = getPriceByZoneOrPriceItself(cleaningPrestation?.member_prices ?? 0, zone);
  const memberHourlyPriceReduction = standardPrestationPrice - memberPrestationPrice;
  const memberReduction = memberHourlyPriceReduction * durationInHours;

  const productPrestationPrice = getPriceByZoneOrPriceItself(productsPrestation?.prices ?? 0, zone);

  const standardPrices = computeCleaningPricesForPrestationPrice({
    cleaningPrestationPrice: standardPrestationPrice,
    productPrestationPrice,
    durationInHours,
    isDurationDefined,
    primaryAppointment,
    universe,
    promotion,
    giftCard,
    tipAmount,
  });

  const membershipPrices = computeCleaningPricesForPrestationPrice({
    cleaningPrestationPrice: memberPrestationPrice,
    productPrestationPrice,
    durationInHours,
    isDurationDefined,
    primaryAppointment,
    universe,
    promotion,
    giftCard,
    tipAmount,
  });

  return {
    kind: 'cleaning',
    ...standardPrices,
    membership: {
      reduction: round(memberReduction),
      hourlyPriceReduction: round(memberHourlyPriceReduction),
      ...membershipPrices,
    },
  };
};

/// NEW CLEANING PRICES CALCULATION ///

type ComputeNewCleaningPricesForPrestationPriceParams = {
  durationInHours: number;
  giftCard?: GiftCard;
  holidays: string[];
  isDurationDefined: boolean;
  optionsHourlyPrice: number;
  prestationHourlyPrice: number;
  prestationHourlyPriceAfterTax: number;
  primaryAppointment: Moment | null;
  promotion?: Promotion | null;
  tipAmount: number;
};

const computeNewCleaningPricesForPrestationPrice = ({
  durationInHours,
  giftCard,
  holidays,
  isDurationDefined,
  optionsHourlyPrice,
  prestationHourlyPrice,
  prestationHourlyPriceAfterTax,
  primaryAppointment,
  promotion,
  tipAmount,
}: ComputeNewCleaningPricesForPrestationPriceParams): ComputeCleaningPricesForPrestationPriceResponse => {
  // We calculate the taxRate based on the price sent from the back
  // If the user_price_after_tax is the same as the user_price, the taxRate will equal to 1
  const taxRate = prestationHourlyPriceAfterTax / prestationHourlyPrice;

  const prestationAndOptionsHourlyPrice = prestationHourlyPrice + optionsHourlyPrice;

  // Total price whithout adjustments for the duration
  const prestationAndOptionsTotalPrice = prestationAndOptionsHourlyPrice * durationInHours;

  // Appointment surcharge
  const appointmentSurchargeReason = getAppointmentSurchargeReason(primaryAppointment, holidays);
  const hasPriceWithSurcharge = prestationAndOptionsTotalPrice > 0 && appointmentSurchargeReason;
  const hourlyPriceSurcharge = hasPriceWithSurcharge ? HOURLY_SURCHARGE : 0;
  const appointmentSurcharge = hourlyPriceSurcharge * durationInHours;

  const totalPriceBeforeReductions = prestationAndOptionsTotalPrice + appointmentSurcharge;

  // Promo and gift card values
  const { promoValue, giftValue } = calculatePromoAndGiftValues({
    priceBeforeReduction: totalPriceBeforeReductions,
    promotion,
    giftCard,
    durationInHours,
  });

  // TODO: This part might need to be discussed again with the Product
  // as it's not clear why we only count the promotion per_hour for this calculation and not the other type of promotion
  const promotionAmountPerHour = promotion?.calculation_type === 'per_hour' ? promotion.amount : 0;

  const totalOfPricesPerHour = Math.max(
    prestationAndOptionsHourlyPrice + hourlyPriceSurcharge - promotionAmountPerHour,
    0,
  );
  // If we use a giftcard, we don't calculate the tax reduction for the hourly price
  // TODO: ask the Product why we don't calculate it for the hourly price but we do for the total price
  const totalOfPricesPerHourAfterTax = giftValue > 0 ? totalOfPricesPerHour : totalOfPricesPerHour * taxRate;

  // Total
  const totalPriceBeforeTax = Math.max(totalPriceBeforeReductions - (promoValue + giftValue), 0);
  // Total price After tax
  const totalPriceAfterTax = totalPriceBeforeTax * taxRate;

  const totalPriceToShow = isDurationDefined ? totalPriceBeforeTax : totalOfPricesPerHour;
  const totalPriceAfterTaxToShow = isDurationDefined ? totalPriceAfterTax : totalOfPricesPerHourAfterTax;
  const taxAmount = totalPriceToShow - totalPriceAfterTaxToShow;

  return {
    appointmentSurcharge: round(appointmentSurcharge),
    appointmentSurchargeReason,
    giftValue: round(giftValue),
    hourlyPrice: round(prestationAndOptionsHourlyPrice),
    hourlyPriceSurcharge: hourlyPriceSurcharge ? round(hourlyPriceSurcharge) : undefined,
    minPriceSurcharge: 0,
    promoValue: round(promoValue),
    taxAmount: round(taxAmount),
    tipAmount: round(tipAmount),
    totalHourlyPrice: round(totalOfPricesPerHour),
    totalHourlyPriceAfterTax: round(totalOfPricesPerHourAfterTax),
    totalPrice: round(totalPriceBeforeTax),
    totalPriceAfterTax: round(totalPriceAfterTaxToShow),
    totalPriceToShow: round(totalPriceToShow),
  };
};

type ComputeNewCleaningPricesParams = {
  cleaningPrestation: CleaningPrestation;
  cleaningPrestationOptions?: CleaningPrestationOption[];
  duration?: number | null;
  giftCard?: GiftCard;
  primaryAppointment: Moment | null;
  promotion?: Promotion | null;
  tipAmount?: number;
  universe: Universe;
};

export const computeNewCleaningPrices = ({
  cleaningPrestation,
  cleaningPrestationOptions,
  duration,
  giftCard,
  primaryAppointment,
  promotion,
  tipAmount = 0,
  universe,
}: ComputeNewCleaningPricesParams): CleaningBasketPrices => {
  const { holidays } = universe;

  // As duration can be undefined/null at beginning of funnel
  const isDurationDefined = !!duration;
  // TODO: Ask the Product if 2 hours by default is the good value by default
  // Duration in hours
  const durationInHours = (duration || 0) / 60 || 2;

  const standardPrestationHourlyPrice = cleaningPrestation.pricing.user_price;
  const standardOptionsHourlyPrice =
    cleaningPrestationOptions?.reduce((sum, option) => sum + option.pricing.user_price, 0) || 0;

  const standardPrices = computeNewCleaningPricesForPrestationPrice({
    durationInHours,
    giftCard,
    holidays,
    isDurationDefined,
    optionsHourlyPrice: standardOptionsHourlyPrice,
    prestationHourlyPrice: standardPrestationHourlyPrice,
    prestationHourlyPriceAfterTax: cleaningPrestation.pricing.user_price_after_tax,
    primaryAppointment,
    promotion,
    tipAmount,
  });

  const membershipPrestationHourlyPrice = cleaningPrestation.pricing.member_price;
  const membershipOptionsHourlyPrice =
    cleaningPrestationOptions?.reduce((sum, option) => sum + option.pricing.member_price, 0) || 0;

  const membershipPrices = computeNewCleaningPricesForPrestationPrice({
    durationInHours,
    giftCard,
    holidays,
    isDurationDefined,
    optionsHourlyPrice: membershipOptionsHourlyPrice,
    prestationHourlyPrice: membershipPrestationHourlyPrice,
    prestationHourlyPriceAfterTax: cleaningPrestation.pricing.member_price_after_tax,
    primaryAppointment,
    promotion,
    tipAmount,
  });

  const memberReductionHourlyPrice =
    standardPrestationHourlyPrice +
    standardOptionsHourlyPrice -
    (membershipPrestationHourlyPrice + membershipOptionsHourlyPrice);
  const memberReduction = memberReductionHourlyPrice * durationInHours;

  return {
    kind: 'cleaning',
    ...standardPrices,
    membership: {
      reduction: round(memberReduction),
      hourlyPriceReduction: round(memberReductionHourlyPrice),
      ...membershipPrices,
    },
  };
};
